import { useState, useEffect, useCallback } from 'react';
import {
  getAttendedDoctors,
  startConversationWithDoctor,
  type AttendedDoctor,
  type StartConversationResult,
} from '@/services/chat';
import { getAuthToken } from '@/utils/auth';

export function useAttendedDoctors() {
  const [doctors, setDoctors] = useState<AttendedDoctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDoctors = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        setDoctors([]);
        setIsLoading(false);
        return;
      }

      const result = await getAttendedDoctors();
      if (result.success) {
        setDoctors(result.data || []);
      } else {
        throw new Error(result.error || 'Erro ao carregar médicos atendentes');
      }
    } catch (err) {
      console.error('Error in useAttendedDoctors:', err);
      setError(
        err instanceof Error ? err.message : 'Erro ao carregar médicos atendentes',
      );
      setDoctors([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const startConversation = useCallback(
    async (doctorId: string): Promise<StartConversationResult> => {
      const result = await startConversationWithDoctor(doctorId);
      if (!result.success || !result.data?.appointmentId) {
        throw new Error(
          result.error ||
            'Não foi possível iniciar a conversa com este médico.',
        );
      }
      return result.data;
    },
    [],
  );

  return {
    doctors,
    isLoading,
    error,
    refresh: fetchDoctors,
    startConversation,
  };
}
